import styled, { css } from 'styled-components'

import {
  Badge as LMFBadge,
  ResponsiveImage,
  fromMd,
  timingDefault,
} from '@syconium/little-miss-figgy'

export const Body = styled.div<{
  $isHeading: boolean | null
  $isFooterVariant: boolean | null
  $isSecondaryText: boolean | null
}>`
  align-items: center;
  display: flex;
  line-height: ${o => (o.$isFooterVariant ? '1' : '1.5')};
  padding: ${o => (!o.$isFooterVariant ? '6px' : o.$isSecondaryText ? '4px' : '10px')} 0;

  ${fromMd} {
    padding: ${o => (!o.$isFooterVariant ? '6px' : o.$isSecondaryText ? '2px' : '5px')} 0;
  }
`

export const Btn = styled.button<{
  $isHeading: boolean | null
  $isSecondaryText: boolean | null
  $isBold: boolean | null
  $isUppercase: boolean
}>`
  border: none;
  padding: 0;
  color: ${o => (o.$isSecondaryText ? o.theme.color.deprecated.stoneDark : 'white')};
  font-size: ${o => (o.$isHeading ? '11px' : '13px')};
  letter-spacing: ${o => (o.$isUppercase ? '0.15em' : '0.05em')};
  font-weight: ${o => (o.$isBold ? 'bold' : 'normal')};

  ${fromMd} {
    font-size: 11px;
    font-weight: ${o => (o.$isHeading ? 'bold' : '500')};
    margin-bottom: ${o => (o.$isHeading ? '24px' : '0')};

    &:hover {
      font-weight: ${o => (o.$isSecondaryText ? '500' : 'bold')};
    }
  }

  span {
    pointer-events: none;
  }
`

// IconWrap prevents layout shift on icon load when content is long
export const IconWrap = styled.div`
  flex-shrink: 0;
  height: 16px;
  width: 28px;

  ${fromMd} {
    width: 24px;
  }
`

export const Icon = styled(ResponsiveImage)`
  height: 16px;
  width: 16px;
`

// NOTE: exists for accessibility reasons
export const RegularWeightHeadingText = styled.h2`
  font-size: 15px;
  font-weight: regular;
  letter-spacing: 0.15em;

  ${fromMd} {
    font-size: 13px;
  }
`

export const CtaHeadingText = styled(RegularWeightHeadingText).attrs({ as: 'span' })`
  font-weight: 700;
  letter-spacing: 0.05em;
`

export const TextWrap = styled.div<{
  textColor?: string | null
  $isB2B?: boolean
}>`
  align-items: center;
  color: ${o => o.textColor};
  display: flex;
  width: 100%;
  ${o =>
    o.$isB2B &&
    css`
      justify-content: space-between;
      flex-wrap: wrap;
      gap: ${o.theme.spacing(2)};

      ${fromMd} {
        max-width: 250px;
      }

      > ${Badge} {
        margin-inline-start: 0;
      }
    `}

  a,
  button {
    position: relative;

    &::after {
      background: currentColor;
      bottom: -4px;
      content: '';
      height: 1px;
      left: 0;
      opacity: 0.7;
      position: absolute;
      transform-origin: left;
      transform: scaleX(0);
      transition: transform ${timingDefault} ease-in;
      width: 100%;
    }

    &:hover::after {
      transform: scaleX(1);
    }

    &[tabindex='0'] {
      cursor: pointer;
    }
  }
`

export const TextWithTrailingIcon = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${o => o.theme.spacing(1)};
`

export const Text = styled.span`
  font-size: 14px;
  font-weight: normal;
  letter-spacing: 0.05em;

  ${fromMd} {
    font-size: 12px;
  }
`

export const HeadingText = styled.h2`
  font-size: 15px;
  font-weight: bold;
  letter-spacing: 0.15em;

  ${fromMd} {
    font-size: 13px;
  }
`

export const FooterText = styled(Text)<{
  $isHeading: boolean | null
  $isSecondaryText: boolean | null
}>`
  color: ${o => (o.$isSecondaryText ? o.theme.color.deprecated.stoneDark : 'white')};
  font-size: ${o => (o.$isHeading ? '11px' : '13px')};
  font-weight: ${o => (o.$isHeading ? 'bold' : '500')};

  ${fromMd} {
    font-size: 11px;
    margin-bottom: ${o => (o.$isHeading ? '24px' : '0')};

    &:hover {
      font-weight: ${o => (o.$isSecondaryText ? '500' : 'bold')};
    }
  }
`

export const Badge = styled(LMFBadge).attrs<{
  $badgeColor: string | null
  $badgeTextColor: string | null
  $isB2B: boolean
}>(o => ({
  variant: 'pill',
  size: 'small',
  colorVariant: o.$isB2B ? 'info' : 'default',
  fontWeight: 700,
  textTransform: 'none',
}))`
  display: inline-block;
  background: ${o =>
    o.$badgeColor ||
    (o.$isB2B
      ? o.theme.color.button.primary.fill.on.background
      : o.theme.color.fill.disabled.subtle)};
  border: 0;
  border-radius: 11px;
  color: ${o =>
    o.$badgeTextColor ||
    (o.$isB2B
      ? o.theme.color.button.primary.content.on.background
      : o.theme.color.text.primary.on.subtle)};
  font-size: 9px;
  line-height: 1;
  letter-spacing: 0;
  margin-inline-start: 8px;
  padding: 5px 9px 4px;
  text-transform: uppercase;
`

export const FooterItemIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: left;
  gap: ${o => o.theme.spacing(2)};
  padding: 3px 0 0 0;

  ${fromMd} {
    padding: 5px 0 0 0;
  }
`

export const FooterItemIcon = styled(ResponsiveImage)`
  display: block;
  max-height: 1rem;
  width: 25px;
  flex: 0 0 auto;
`

export const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;
  text-align: start;
`

export const PreviewPanelWrap = styled.div`
  padding: ${o => o.theme.spacing(1, 0)};
  width: 100%;
`
